/**
 * Phase-aware progress, in the game's voice.
 *
 * Three rules, and all three are about what this is *not*:
 *
 * - **Not an overlay.** It is one dimmed line at the foot of the feed. A modal spinner is
 * what made a fifty-second turn feel like an outage; a line of text at the bottom of a
 * readable story is the same information with none of the hostage-taking.
 * - **Not a progress bar.** There is no percentage to show. Phase A's duration depends on how
 * many tool round-trips the DM takes and cannot be predicted, so a bar would be a lie drawn
 * at 60fps. An elapsed counter is the honest version, and it appears only once the wait is
 * long enough to be worth acknowledging.
 * - **Not our words.** `progress.label` is written by the DM loop (`quest/dm/turn.py`) and is
 * already in the game's register. The client renders it and does not paraphrase it; `phase`
 * is typed as a plain string precisely so a phase this client has never heard of still
 * renders (see `KNOWN_TURN_PHASES`).
 */
import { ELAPSED_VISIBLE_MS, PHASE_LINE_ID, formatElapsed } from '@/features/turn/phase';
import { useT } from '@/i18n/context';

interface PhaseLineProps {
  /** `progress.label`. Null before the first `progress` event, which is a few hundred ms. */
  label: string | null;
  elapsedMs: number;
  /** Three missed heartbeats. Worth saying; never worth aborting for. */
  stalled: boolean;
}

export function PhaseLine({ label, elapsedMs, stalled }: PhaseLineProps) {
  const t = useT();

  return (
    <p
      id={PHASE_LINE_ID}
      /*
       * **Not a live region, as of the accessibility pass.** It used to be one, politely — and the elapsed
       * counter beside it changes every second, so a screen reader re-read the whole line
       * once a second for the length of the turn. The spoken track belongs to
       * `TurnAnnouncer`, which says the phase label when it *changes* and says nothing in
       * between; this line stays in the accessibility tree as the action bar's
       * `aria-describedby` target, where it is read on focus rather than on a timer.
       */
      className="text-fg-faint mt-[var(--space-chapter)] flex flex-wrap items-center gap-x-3 font-mono"
      style={{ fontSize: 'var(--text-meta)' }}
    >
      {/*
        `label` is the server's own sentence, arriving on the `progress` event, and it comes
        in the story's language: `routers/turns.py` injects a labeller bound to
        `story.locale` that joins `ui.turn.phase.*` out of the same bundle this client reads.
        The fallback below is ours, for the few hundred ms before the first `progress` event.
      */}
      <span className="text-fg-muted">{label ?? t('ui.turn.reading_action')}</span>
      {elapsedMs >= ELAPSED_VISIBLE_MS ? <span>{formatElapsed(elapsedMs)}</span> : null}
      {stalled ? <span>{t('ui.turn.still_working')}</span> : null}
    </p>
  );
}
